import {Audio} from '@remotion/media';
import React from 'react';
import {AbsoluteFill, Easing, interpolate, staticFile, useCurrentFrame} from 'remotion';

const SANS = '-apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Arial, sans-serif';
const SERIF = '"Songti SC", "STSong", "Noto Serif SC", serif';
const MONO = '"SF Mono", "Menlo", "Courier New", monospace';
const CLAMP = {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'} as const;
const GROUND = 872;

const mulberry32 = (seed: number) => () => {
	seed |= 0;
	seed = (seed + 0x6d2b79f5) | 0;
	let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
	t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
	return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

type Building = {
	x: number;
	w: number;
	h: number;
	windows: string;
	winOpacity: number;
	body: string;
	sign?: {text: string; color: string; vertical: boolean};
};

const buildLayer = (
	seed: number,
	count: number,
	minH: number,
	maxH: number,
	body: string,
	windowColor: string,
	signs: {text: string; color: string; vertical: boolean}[],
): Building[] => {
	const rand = mulberry32(seed);
	const out: Building[] = [];
	let x = -160;
	for (let i = 0; i < count; i++) {
		const w = 90 + Math.floor(rand() * 130);
		const h = minH + Math.floor(rand() * (maxH - minH));
		const winOpacity = 0.22 + rand() * 0.5;
		const sign = rand() > 0.55 ? signs[Math.floor(rand() * signs.length)] : undefined;
		out.push({x, w, h, windows: windowColor, winOpacity, body, sign});
		x += w + 24 + Math.floor(rand() * 90);
	}
	return out;
};

const SIGN_POOL = [
	{text: '霓虹', color: '#ff3d9a', vertical: false},
	{text: '旅館', color: '#35e0ff', vertical: true},
	{text: '電玩', color: '#ffb347', vertical: false},
	{text: '深夜食堂', color: '#ff3d9a', vertical: false},
	{text: '24H', color: '#35e0ff', vertical: false},
	{text: '藥', color: '#ffb347', vertical: true},
	{text: 'KTV', color: '#ff3d9a', vertical: false},
	{text: '雨', color: '#35e0ff', vertical: true},
];

const FAR = buildLayer(11, 16, 170, 330, '#161c40', 'rgba(255,196,120,0.75)', []);
const MID = buildLayer(22, 14, 300, 540, '#121736', 'rgba(120,220,255,0.8)', SIGN_POOL);
const NEAR = buildLayer(33, 10, 430, 690, '#0c1024', 'rgba(255,180,110,0.85)', SIGN_POOL);

const Buildings: React.FC<{layer: Building[]; pan: number; blur: number; dim: number}> = ({layer, pan, blur, dim}) => (
	<div style={{position: 'absolute', inset: 0, filter: blur ? `blur(${blur}px)` : 'none', opacity: dim}}>
		{layer.map((b, i) => (
			<div key={i} style={{position: 'absolute', left: b.x - pan, top: GROUND - b.h, width: b.w, height: b.h}}>
				<div style={{position: 'absolute', inset: 0, backgroundColor: b.body}} />
				<div
					style={{
						position: 'absolute',
						left: 10,
						right: 10,
						top: 16,
						bottom: 12,
						backgroundColor: i % 3 === 0 ? 'rgba(120,220,255,0.8)' : b.windows,
						opacity: b.winOpacity * 0.85,
						WebkitMaskImage:
							'repeating-linear-gradient(90deg, #000 0 5px, transparent 5px 15px), repeating-linear-gradient(0deg, #000 0 4px, transparent 4px 17px)',
						WebkitMaskComposite: 'source-in',
						maskComposite: 'intersect',
					}}
				/>
				<div
					style={{
						position: 'absolute',
						inset: 0,
						background: 'linear-gradient(180deg, rgba(200,220,255,0.14), transparent 26%)',
					}}
				/>
				{b.sign ? (
					<div
						style={{
							position: 'absolute',
							right: 10,
							top: 26,
							padding: b.sign.vertical ? '10px 6px' : '6px 12px',
							border: `2px solid ${b.sign.color}`,
							borderRadius: 6,
							color: b.sign.color,
							fontFamily: SANS,
							fontSize: b.sign.vertical ? 20 : 22,
							fontWeight: 700,
							letterSpacing: b.sign.vertical ? 0 : 4,
							writingMode: b.sign.vertical ? 'vertical-rl' : 'horizontal-tb',
							textShadow: `0 0 12px ${b.sign.color}, 0 0 30px ${b.sign.color}`,
							boxShadow: `0 0 14px ${b.sign.color}66, inset 0 0 10px ${b.sign.color}44`,
							backgroundColor: 'rgba(6,8,16,0.55)',
						}}
					>
						{b.sign.text}
					</div>
				) : null}
			</div>
		))}
	</div>
);

const RAIN_A = Array.from({length: 220}, (_, i) => ({
	x: (i * 137) % 1980,
	speed: 19 + (i % 7) * 4,
	len: 34 + (i % 5) * 20,
	opacity: 0.1 + (i % 4) * 0.07,
	width: i % 3 === 0 ? 2 : 1,
}));
const RAIN_B = Array.from({length: 150}, (_, i) => ({
	x: (i * 211) % 1980,
	speed: 30 + (i % 5) * 6,
	len: 60 + (i % 4) * 26,
	opacity: 0.06 + (i % 3) * 0.05,
	width: 2,
}));

const Rain: React.FC = () => {
	const frame = useCurrentFrame();
	return (
		<div style={{position: 'absolute', inset: 0, overflow: 'hidden'}}>
			{RAIN_A.map((d, i) => {
				const y = ((i * 83 + frame * d.speed) % 1500) - 260;
				return (
					<div
						key={`a${i}`}
						style={{
							position: 'absolute',
							left: d.x + y * 0.08,
							top: y,
							width: d.width,
							height: d.len,
							backgroundColor: `rgba(190,220,255,${d.opacity})`,
							transform: 'rotate(9deg)',
						}}
					/>
				);
			})}
			{RAIN_B.map((d, i) => {
				const y = ((i * 127 + frame * d.speed) % 1600) - 300;
				return (
					<div
						key={`b${i}`}
						style={{
							position: 'absolute',
							left: d.x + y * 0.09,
							top: y,
							width: d.width,
							height: d.len,
							backgroundColor: `rgba(160,200,255,${d.opacity})`,
							transform: 'rotate(9deg)',
							filter: 'blur(1px)',
						}}
					/>
				);
			})}
		</div>
	);
};

const SPLASH = Array.from({length: 70}, (_, i) => ({
	x: (i * 173) % 1920,
	y: 890 + ((i * 61) % 170),
	phase: (i * 13) % 26,
}));

const Road: React.FC = () => {
	const frame = useCurrentFrame();
	return (
		<div style={{position: 'absolute', left: 0, right: 0, top: GROUND, bottom: 0}}>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					background: 'linear-gradient(180deg, rgba(22,26,52,0.95) 0%, rgba(8,10,22,1) 55%, rgba(4,5,12,1) 100%)',
				}}
			/>
			<div
				style={{
					position: 'absolute',
					left: 0,
					right: 0,
					top: 0,
					height: 150,
					background:
						'linear-gradient(90deg, rgba(255,61,154,0.12), rgba(53,224,255,0.1), rgba(255,179,71,0.12), rgba(255,61,154,0.1))',
					filter: 'blur(26px)',
					opacity: 0.7,
				}}
			/>
			{SPLASH.map((s, i) => {
				const phase = (frame + s.phase) % 26;
				const visible = phase < 7;
				return (
					<div
						key={i}
						style={{
							position: 'absolute',
							left: s.x,
							top: s.y - GROUND,
							width: 14 + (phase % 3) * 4,
							height: 3,
							borderRadius: '50%',
							backgroundColor: 'rgba(190,220,255,0.5)',
							opacity: visible ? (1 - phase / 7) * 0.55 : 0,
						}}
					/>
				);
			})}
		</div>
	);
};

const TRAILS = [
	{start: -300, speed: 13, y: 24, w: 380, color: '#ff5a4a'},
	{start: 500, speed: 9, y: 52, w: 300, color: '#ffd9a0'},
	{start: 1100, speed: 15, y: 30, w: 420, color: '#ff5a4a'},
	{start: 1700, speed: 11, y: 64, w: 340, color: '#ffd9a0'},
	{start: 200, speed: 17, y: 96, w: 260, color: '#ff5a4a'},
	{start: 900, speed: 12, y: 120, w: 400, color: '#9fe8ff'},
	{start: 1500, speed: 14, y: 146, w: 320, color: '#ff5a4a'},
	{start: 60, speed: 10, y: 170, w: 360, color: '#ffd9a0'},
];

const Trails: React.FC = () => {
	const frame = useCurrentFrame();
	return (
		<div style={{position: 'absolute', left: 0, right: 0, top: GROUND, bottom: 0, overflow: 'hidden'}}>
			{TRAILS.map((t, i) => {
				const x = ((t.start + frame * t.speed) % 2500) - 420;
				return (
					<div
						key={i}
						style={{
							position: 'absolute',
							left: x,
							top: t.y,
							width: t.w,
							height: 7,
							borderRadius: 5,
							background: `linear-gradient(90deg, transparent, ${t.color}, transparent)`,
							boxShadow: `0 0 34px ${t.color}, 0 0 90px ${t.color}88`,
							opacity: 1,
						}}
					/>
				);
			})}
		</div>
	);
};

const Caption: React.FC<{text: string; start: number; end: number}> = ({text, start, end}) => {
	const frame = useCurrentFrame();
	const opacity = interpolate(frame, [start, start + 12, end - 12, end], [0, 1, 1, 0], CLAMP);
	return (
		<div
			style={{
				position: 'absolute',
				left: 0,
				right: 0,
				bottom: 132,
				textAlign: 'center',
				fontFamily: SANS,
				fontSize: 44,
				fontWeight: 500,
				letterSpacing: 6,
				color: '#eaf2ff',
				textShadow: '0 0 18px rgba(53,224,255,0.55), 0 2px 26px rgba(0,0,0,0.9)',
				opacity,
			}}
		>
			{text}
		</div>
	);
};

export const NeonRain: React.FC = () => {
	const frame = useCurrentFrame();
	const t = frame / 30;
	const farPan = 26 * t;
	const midPan = 52 * t;
	const nearPan = 88 * t;
	const wobbleX = Math.sin(frame / 47) * 3;
	const wobbleY = Math.sin(frame / 61) * 2;

	const flash1 = interpolate(frame, [150, 152, 156], [0, 0.26, 0], CLAMP);
	const flash2 = interpolate(frame, [330, 332, 336], [0, 0.22, 0], CLAMP);
	const flash = Math.max(flash1, flash2);

	const seconds = Math.floor(t) + 3;
	const clock = `02:17:${String(seconds).padStart(2, '0')}`;

	const titleP = interpolate(frame, [388, 404], [0, 1], {...CLAMP, easing: Easing.out(Easing.cubic)});
	const flicker = frame % 47 < 3 ? 0.55 : frame % 23 < 2 ? 0.75 : 1;

	return (
		<AbsoluteFill style={{backgroundColor: '#05060d', fontFamily: SANS, overflow: 'hidden'}}>
			<Audio src={staticFile('neon.wav')} />

			<div style={{position: 'absolute', inset: 0, transform: `translate(${wobbleX}px, ${wobbleY}px) scale(1.02)`}}>
				<div
					style={{
						position: 'absolute',
						inset: 0,
						background:
							'radial-gradient(70% 60% at 50% 78%, rgba(90,40,120,0.35), transparent 70%), linear-gradient(180deg, #0a0c20 0%, #101538 55%, #1a1140 78%, #05060d 100%)',
					}}
				/>
				<Buildings layer={FAR} pan={farPan} blur={3} dim={0.6} />
				<Buildings layer={MID} pan={midPan} blur={1} dim={0.85} />
				<div
					style={{
						position: 'absolute',
						left: 0,
						right: 0,
						top: GROUND - 120,
						height: 200,
						background: 'linear-gradient(180deg, transparent, rgba(60,80,180,0.22))',
						filter: 'blur(18px)',
					}}
				/>
				<Buildings layer={NEAR} pan={nearPan} blur={0} dim={1} />
				<Road />
				<Trails />
				<div
					style={{
						position: 'absolute',
						left: 0,
						right: 0,
						top: GROUND,
						height: 190,
						overflow: 'hidden',
					}}
				>
					<div style={{position: 'absolute', inset: 0, opacity: 0.38, filter: 'blur(3px)'}}>
						<div style={{position: 'absolute', left: 0, right: 0, top: 0, height: 1080, transform: 'scaleY(-1)', transformOrigin: 'top'}}>
							<div style={{position: 'absolute', left: 0, right: 0, top: 0, height: 1080, transform: `translateY(-${GROUND}px)`}}>
								<Buildings layer={NEAR} pan={nearPan} blur={0} dim={0.85} />
								<Buildings layer={MID} pan={midPan} blur={1} dim={0.45} />
							</div>
						</div>
					</div>
				</div>
				<div
					style={{
						position: 'absolute',
						left: 0,
						right: 0,
						top: GROUND,
						height: 190,
						background: 'linear-gradient(to bottom, rgba(5,6,13,0) 0%, rgba(5,6,13,0.88) 55%, rgba(4,5,12,1) 100%)',
					}}
				/>
				<Rain />
			</div>

			<div
				style={{
					position: 'absolute',
					inset: 0,
					background: 'radial-gradient(70% 60% at 50% 50%, transparent 45%, rgba(2,3,8,0.75) 100%)',
				}}
			/>
			<div style={{position: 'absolute', inset: 0, backgroundColor: '#cfe4ff', opacity: flash, mixBlendMode: 'screen'}} />

			<div
				style={{
					position: 'absolute',
					left: 84,
					top: 72,
					fontFamily: MONO,
					fontSize: 22,
					letterSpacing: 3,
					color: 'rgba(160,230,255,0.85)',
					textShadow: '0 0 14px rgba(53,224,255,0.6)',
				}}
			>
				{clock}
			</div>
			<div
				style={{
					position: 'absolute',
					right: 84,
					top: 74,
					fontFamily: MONO,
					fontSize: 15,
					letterSpacing: 3,
					color: 'rgba(200,220,255,0.45)',
				}}
			>
				雨量 12mm · 19°C · 槐安 · NEON DISTRICT
			</div>

			<Caption text="凌晨 2:17，这座城市还在营业" start={30} end={120} />
			<Caption text="雨把霓虹揉碎在柏油路上" start={156} end={258} />
			<Caption text="有人还在等一班不会来的车" start={288} end={378} />

			<div
				style={{
					position: 'absolute',
					left: 0,
					right: 0,
					bottom: 300,
					textAlign: 'center',
					opacity: titleP,
					transform: `translateY(${(1 - titleP) * 24}px)`,
				}}
			>
				<div
					style={{
						fontFamily: SERIF,
						fontSize: 132,
						fontWeight: 700,
						letterSpacing: 26,
						color: '#f4f8ff',
						opacity: flicker,
						textShadow:
							'0 0 18px rgba(255,61,154,0.9), 0 0 46px rgba(53,224,255,0.75), 0 0 90px rgba(255,61,154,0.5)',
					}}
				>
					霓虹雨夜
				</div>
				<div
					style={{
						marginTop: 18,
						fontFamily: MONO,
						fontSize: 22,
						letterSpacing: 12,
						color: 'rgba(160,230,255,0.8)',
						textShadow: '0 0 16px rgba(53,224,255,0.6)',
					}}
				>
					NEON RAIN NIGHT
				</div>
			</div>

			<div
				style={{
					position: 'absolute',
					right: 84,
					bottom: 60,
					fontFamily: MONO,
					fontSize: 12,
					letterSpacing: 3,
					color: 'rgba(200,220,255,0.3)',
					opacity: titleP,
				}}
			>
				PROCEDURAL CITY · REMOTION 4
			</div>
		</AbsoluteFill>
	);
};
